import { forwardRef } from 'react';
import { Divider, IconArrowRightBold, IconTimeBold, Text } from '@croquiscom-pvt/zds';
import styled from '@emotion/styled';

import { FloatBannerInfo } from '@domains/pdp-common';

import { RollingNumber } from './Counting/CountingAnimation';
interface Props {
  children: React.ReactNode;
  bannerInfo: FloatBannerInfo | null | undefined;
  countdown: string;
  showTimerIcon: boolean;
  onHeaderClick?: () => void;
}

export const PromotionLayout = forwardRef<HTMLDivElement, Props>(
  ({ children, bannerInfo, countdown, showTimerIcon, onHeaderClick }, ref) => {
    const pointColor = bannerInfo?.point_color?.normal ?? '';
    const contentsBackgroundColor = bannerInfo?.background_color?.normal ?? '';
    const badgeColor = bannerInfo?.badge_color?.normal ?? '';
    const isHmsCountdown = /(?<!\d)\d{2}:\d{2}:\d{2}(?!\d)/.test(countdown);
    const isDaysCountdown = /^\d+일$/.test(countdown);
    const shouldShowCountdownSuffix = isHmsCountdown || isDaysCountdown;

    return (
      <Container ref={ref}>
        <Header style={{ backgroundColor: pointColor }} onClick={onHeaderClick}>
          <WrapImage>
            <Image src={bannerInfo?.image_url?.normal || ''} alt={bannerInfo?.promotion_name || ''} />
            <IconArrowRightBold size={18} color='iconStrongInverse' />
          </WrapImage>
        </Header>
        <Contents style={{ backgroundColor: contentsBackgroundColor }}>
          {children}
          <Divider color='dividerThin' spacing='14px' style={{ padding: '0 16px' }} />
          <PromotionText as='div' variant='Body_16_Multiline_Medium' color='textStrong'>
            <TimeText>
              {showTimerIcon && <IconTimeBold size={20} color='iconPrimary' style={{ marginRight: '2px' }} />}
              <Text
                variant='Body_14_Multiline_Medium'
                color='textPrimary'
                style={{ width: isHmsCountdown ? '59px' : undefined }}
              >
                {countdown}
              </Text>
              {shouldShowCountdownSuffix && (
                <Text variant='Body_14_Multiline_Medium' color='textPrimary' style={{ whiteSpace: 'break-spaces' }}>
                  {' '}
                  후
                </Text>
              )}
            </TimeText>
            <BadgeText
              as='div'
              variant='Body_15_Multiline_Bold'
              style={{ color: pointColor, backgroundColor: badgeColor }}
            >
              {Boolean(bannerInfo?.price_diff) && (
                <>
                  <RollingNumber value={bannerInfo?.price_diff ?? 0} />
                  {bannerInfo?.postfix_badge_text}
                </>
              )}
              {bannerInfo?.icon_url?.normal && (
                <img
                  style={{ height: 20, width: 'auto', display: 'block', flexShrink: 0 }}
                  src={bannerInfo?.icon_url?.normal || ''}
                  alt='위 화살표'
                />
              )}
            </BadgeText>
          </PromotionText>
        </Contents>
      </Container>
    );
  },
);

const Container = styled.div`
  margin: 16px;
  border-radius: 6px;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 0 16px 0 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  cursor: pointer;
`;

const WrapImage = styled.div`
  display: flex;
  align-items: center;
`;

const Image = styled.img`
  width: auto;
  height: 20px;
  object-fit: cover;
`;

const Contents = styled.div`
  padding: 16px;
`;

const PromotionText = styled(Text)`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const BadgeText = styled(Text)`
  padding: 0 6px 0 8px;
  height: 28px;
  display: flex;
  align-items: center;
  gap: 2px;
  border-radius: 2px;
  white-space: pre;
`;

const TimeText = styled.div`
  display: flex;
  align-items: center;
`;
